import React, { Fragment, useContext, useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, CardBody, Table } from 'reactstrap';
import { Breadcrumbs } from '../../../../AbstractElements';
import { DateCreated, DateModified, DeleteSuccess, Description, Impact, Priority, PriorityColor, Status, Urgency, Weight, description } from '../../../../Constant';
import HeaderCard from '../../../Common/Component/HeaderCard';
import CustomizerContext from '../../../../_helper/Customizer';
import { ImpactApi, PriorityApi, UrgencyApi } from '../../../../api';
import { toast } from 'react-toastify';
import DataTableComponent from '../../../Common/Component/DataTableComponent';
import ViewModal from '../../../Common/Component/ViewModal';
import { base_form_fields, color_field, createDropdownField, dynamic_field } from '../../../../Data/FormField/PublicData';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
//import SweetAlert from 'sweetalert2';
import { formatDateWithTimeZone } from '../../../../_helper/dateHelper';
import useAuth from '../../../../Hooks/useAuth';

const PriorityList = () => {
  const { layoutURL } = useContext(CustomizerContext);
  const axiosPrivate = useAxiosPrivate()
  const { userOrganisationId } = useAuth();
  const [formFields, setFormFields] = useState(base_form_fields(`${Priority}`))
  const [IsNewFieldAdded, setIsNewFieldAdded] = useState(false)

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const getAllPrioritiesData = async () => {
    try {
      await axiosPrivate.get(`${PriorityApi}?organisation_id=${userOrganisationId}`).then((resp) => {
        setAllData(resp.data);
        //setIsLoading(false);
      });
    } catch (error) {
      toast.error(error.response.data.detail)
      //setIsLoading(false);
    } finally {
      // Set loading to false after data is fetched (regardless of success or failure)
      setIsLoading(false);
    }
  };

  const ImpactDropdownField = createDropdownField(axiosPrivate,
    'impact_id',
    'impact_id',
    `${Impact}`,
    `${ImpactApi}?organisation_id=${userOrganisationId}`,
    '', 12
  );
  const UrgencyDropdownField = createDropdownField(axiosPrivate,
    'urgency_id',
    'urgency_id',
    `${Urgency}`,
    `${UrgencyApi}?organisation_id=${userOrganisationId}`,
    '', 12
  );

  useEffect(() => {

    const fetchData = async () => {
      try {
        if (!IsNewFieldAdded) {

          const updatedFields = [ImpactDropdownField, UrgencyDropdownField, ...formFields];
          const indexOfName = updatedFields.findIndex((formField) => formField.id === 'description');
          updatedFields.splice(indexOfName + 1, 0, color_field(`${PriorityColor}`));
          updatedFields.splice(indexOfName + 2, 0, dynamic_field('weight', `${Weight}`, 'number'));

          await updatedFields[0].fetchOptions();
          await UrgencyDropdownField.fetchOptions();

          setFormFields(updatedFields);
          setIsNewFieldAdded(true);
        }
      } catch (error) {
        //console.error('Error fetching options:', error);
      }
    };

    fetchData();
  }, [formFields, IsNewFieldAdded, ImpactDropdownField, UrgencyDropdownField, color_field]);


  useEffect(() => {
    const abortController = new AbortController();
    getAllPrioritiesData();

    return () => {
      abortController.abort();
    };
  }, [setAllData]);

  const handleView = (row) => {
    setViewModalOpen(true);
    setEditMode(false);
    setSelectedRowData(row);
  };

  const handleEdit = (row) => {
    // Handle the Edit action
    setViewModalOpen(true);
    setEditMode(true);
    setSelectedRowData(row);
  };

  const handleEditRec = async (id, editRecord) => {
    //console.log(editRecord)
    //return false
    const editRecordTemp = {
      organisation_id: userOrganisationId,
      impact_id: editRecord.impact_id,
      urgency_id: editRecord.urgency_id,
      name: editRecord.name,
      color: editRecord.color,
      weight: editRecord.weight,
      description: editRecord.description,
      is_active: editRecord.is_active,
    };

    try {
      await axiosPrivate.put(`${PriorityApi}/${id}`, editRecordTemp).then((resp) => {
        const updatedData = allData.map((row) =>
          row.id === selectedRowData.id ? { ...row, ...resp.data } : row
        );

        setAllData(updatedData);

        toast.success(`${resp.data.name} is successfully updated.`)
        handleCloseModal()
      });
    } catch (error) {
      if (error.response && error.response.status === 422) {
        toast.error('Validation Errors');
      } else if (error.response && error.response.status === 400) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Form submission error');
      }
      throw error;
    }
  };

  const memoizedHandleEdit = useMemo(() => handleEditRec, [allData, selectedRowData]);

  const handleCloseModal = () => {
    setViewModalOpen(false);
  };

  const handleDelete = async (row) => {
    // Prompt for confirmation
    const shouldDelete = window.confirm('Are you sure you want to delete this record?');

    if (!shouldDelete) {
      return; // Do nothing if the user cancels the deletion
    }

    try {
      const response = await axiosPrivate.delete(`${PriorityApi}/${row.id}`);

      if (response.status === 204) {
        setAllData((prevData) => prevData.filter((item) => item.id !== row.id));
        toast.success(DeleteSuccess);
      } else {
        toast.error('Delete request failed:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting record:', error);
    }
  };

  const addBtn = `${process.env.PUBLIC_URL}/crm/settings/priority/create/${layoutURL}`;

  const tableColumns = [
    {
      name: `${Impact}`,
      selector: row => `${row.impact.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Urgency}`,
      selector: row => `${row.urgency.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Priority}`,
      selector: row => `${row.name}`,
      sortable: true,
      center: false,
    },
    {
      name: `${PriorityColor}`,
      selector: row => `${row.color}`,
      sortable: true,
      center: false,
      cell: row => (<span style={{color: row.color}}><i className="fa fa-flag"></i></span>)
    },
    {
      name: `${Weight}`,
      selector: row => `${row.weight}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Description}`,
      selector: row => `${row.description}`,
      sortable: true,
      center: false,
    },
    {
      name: `${Status}`,
      selector: row => row.is_active,
      sortable: true,
      center: false,
      cell: row => (row.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>)
    },
    {
      name: `${DateCreated}`,
      selector: row => `${formatDateWithTimeZone(row.created_at)}`,
      sortable: true,
      center: false,
    },
    {
      name: `${DateModified}`,
      selector: row => `${formatDateWithTimeZone(row.updated_at)}`,
      sortable: true,
      center: false,
    },
  ];

  return (
    <Fragment>
      <Breadcrumbs parent="CRM" title="Priority " mainTitle="Priority" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <HeaderCard title="List" btnUrl={addBtn} />
              <CardBody>
                <DataTableComponent
                  tabledata={allData}
                  tableColumns={tableColumns}
                  isLoading={isLoading}
                  onView={handleView}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        <ViewModal
          isOpen={viewModalOpen}
          toggle={() => setViewModalOpen(!viewModalOpen)}
          rowData={selectedRowData}
          formFields={formFields}
          editMode={editMode}
          onEdit={memoizedHandleEdit}
        >
          {selectedRowData && (
            <>
              <Table>
                <tbody>
                <tr className='border-bottom-primary'>
                    <th scope='row'>{Impact}</th>
                    <td>{selectedRowData.impact.name}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Urgency}</th>
                    <td>{selectedRowData.urgency.name}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{Priority}</th>
                    <td>{selectedRowData.name}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{PriorityColor}</th>
                    <td><span style={{color: selectedRowData.color}}><i className="fa fa-flag"></i></span></td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{description}</th>
                    <td>{selectedRowData.description}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{Status}</th>
                    <td>{selectedRowData.is_active ? <span className="badge badge-light-success">Active</span> : <span className="badge badge-light-danger">Inactive</span>}</td>
                  </tr>
                  <tr className='border-bottom-primary'>
                    <th scope='row'>{DateCreated}</th>
                    <td>{formatDateWithTimeZone(selectedRowData.created_at)}</td>
                  </tr>
                  <tr className='border-bottom-secondary'>
                    <th scope='row'>{DateModified}</th>
                    <td>{formatDateWithTimeZone(selectedRowData.updated_at)}</td>
                  </tr>
                </tbody>
              </Table>
            </>
          )}

        </ViewModal>
      </Container>
    </Fragment>
  );
};

export default PriorityList;